import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { CJEvent } from 'types/event';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from 'utils/request';
import { format } from 'utils/time/format';
import { EVENTS_KEY } from 'containers/CahierJournal/queries/useCJSeances';
import groupBy from 'utils/arrays/groupBy';
import { addToCJPastAtom } from 'containers/CahierJournal/queries/historyAtoms';
import useNotification from 'containers/App/Notifications/useNotification';
import { useAtom } from 'jotai';
import { CAHIER_JOURNAL_DAYS_WITH_EVENTS_KEY } from 'containers/CahierJournal/queries/useGetDaysWithEvents';
import { useTranslation } from 'react-i18next';

export function useDeleteCJSeances(disableUndoRedo?: boolean) {
  const { schoolyear, school } = useSchoolSchoolyear();
  const { notifyError } = useNotification();

  const queryClient = useQueryClient();
  const [, addToPast] = useAtom(addToCJPastAtom);
  const { t } = useTranslation(['cahierJournal']);
  const { mutateAsync } = useMutation({
    mutationFn: (cjEvents: CJEvent[]) => {
      const requestURL = `/schoolyears/${schoolyear.id}/schools/${school.id}/cahier-journal/seances`;
      return request(requestURL, {
        method: 'DELETE',
        body: { ids: cjEvents.map(e => e.id) },
      });
    },
    onMutate: cjEvents => {
      if (!disableUndoRedo) {
        addToPast({
          payload: cjEvents,
          action: 'DELETE',
        });
      }
      Object.entries(groupBy(cjEvents, e => format(e.start, 'fr-FR'))).forEach(
        ([d, newEvents]) => {
          const deletedEventsIds = newEvents.map(e => e.id);
          queryClient.setQueryData<CJEvent[]>(
            [schoolyear.id, school.id, EVENTS_KEY, d],
            old => (old || []).filter(e => !deletedEventsIds.includes(e.id)),
          );
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          schoolyear.id,
          school.id,
          CAHIER_JOURNAL_DAYS_WITH_EVENTS_KEY,
        ],
      });
    },
    onError: (_, seances) => {
      queryClient.invalidateQueries({
        queryKey: [
          schoolyear.id,
          school.id,
          CAHIER_JOURNAL_DAYS_WITH_EVENTS_KEY,
        ],
      });
      notifyError({
        title: t('cahierJournal:messages.deleteError', {
          count: (seances || []).length,
        }),
      });
    },
  });
  return mutateAsync;
}
